import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, MapPin } from 'lucide-react'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { propertiesService } from '../../services/propertiesService'
import { loadGoogleMaps } from '../../helpers/googleMaps'
import { CZECHIA_BOUNDS, getViewportQueries, isWholeCountrySelection } from '../../helpers/mapViewport.mjs'
import { getRegionLabel } from '../../helpers/propertyTypeHelpers'

const formatPrice = (value) => Number.isFinite(value) ? `${value.toLocaleString('cs-CZ')} Kč` : 'Cena na dotaz'

const createInfoContent = (point) => {
  const content = document.createElement('div')
  content.style.cssText = 'width:220px;font-family:Arial,sans-serif;line-height:1.35;cursor:pointer;'

  if (point.image) {
    const image = document.createElement('img')
    image.src = point.image
    image.alt = point.title || 'Nemovitost'
    image.style.cssText = 'display:block;width:100%;height:110px;object-fit:cover;border-radius:6px;margin-bottom:8px;'
    content.appendChild(image)
  }

  const title = document.createElement('div')
  title.textContent = point.title || 'Nemovitost'
  title.style.cssText = 'font-size:13px;font-weight:700;color:#0f172a;margin-bottom:4px;'
  content.appendChild(title)

  const location = document.createElement('div')
  location.textContent = point.location || ''
  location.style.cssText = 'font-size:12px;color:#64748b;margin-bottom:5px;'
  content.appendChild(location)

  const price = document.createElement('div')
  price.textContent = point.m2Price
    ? `${formatPrice(point.price)} · ${point.m2Price.toLocaleString('cs-CZ')} Kč/m²`
    : formatPrice(point.price)
  price.style.cssText = 'font-size:12px;font-weight:600;color:#1e293b;'
  content.appendChild(price)

  return content
}

const geocode = (geocoder, address) => new Promise((resolve) => {
  geocoder.geocode({ address, region: 'CZ' }, (results, status) => {
    resolve(status === 'OK' ? results?.[0]?.geometry || null : null)
  })
})

const fitToPoints = (map, points) => {
  const bounds = new window.google.maps.LatLngBounds()
  points.forEach((point) => bounds.extend(point.coordinates))
  if (bounds.isEmpty()) {
    map.fitBounds(CZECHIA_BOUNDS, 24)
    return
  }
  if (points.length === 1) {
    map.setCenter(points[0].coordinates)
    map.setZoom(14)
    return
  }
  map.fitBounds(bounds, 36)
}

const MapPage = ({
  offerType,
  type,
  region,
  municipalities = [],
  districts = [],
  layouts = [],
  displayOptions = [],
  priceRange,
  search = '',
}) => {
  const navigate = useNavigate()
  const elementRef = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef([])
  const clustererRef = useRef(null)
  const infoWindowRef = useRef(null)
  const viewportRequestRef = useRef(0)
  const [mapReady, setMapReady] = useState(false)
  const [points, setPoints] = useState([])
  const [meta, setMeta] = useState({ total: 0, truncated: false })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const municipalitiesKey = municipalities.join(',')
  const districtsKey = districts.join(',')
  const layoutsKey = layouts.join(',')
  const displayOptionsKey = displayOptions.join(',')
  const minPrice = priceRange?.min || 0
  const maxPrice = priceRange?.max ?? 30000000

  useEffect(() => {
    let current = true
    setLoading(true)
    setError('')

    propertiesService.fetchPropertyMapPoints({
      offerType,
      type,
      region,
      municipalities,
      districts,
      layouts,
      displayOptions,
      priceRange: { min: minPrice, max: maxPrice },
      search,
    }).then((data) => {
      if (!current) return
      setPoints(data.points || [])
      setMeta({ total: data.total || 0, truncated: data.truncated === true })
    }).catch((requestError) => {
      if (!current) return
      console.error('Unable to load dashboard map points:', requestError)
      setPoints([])
      setMeta({ total: 0, truncated: false })
      setError('Nabídky pro mapu se nepodařilo načíst.')
    }).finally(() => {
      if (current) setLoading(false)
    })

    return () => { current = false }
    // Stable string keys avoid repeating the request for equivalent arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerType, type, region, municipalitiesKey, districtsKey, layoutsKey, displayOptionsKey, minPrice, maxPrice, search])

  useEffect(() => {
    let current = true
    loadGoogleMaps().then(() => {
      if (!current || !elementRef.current) return
      mapRef.current = new window.google.maps.Map(elementRef.current, {
        center: { lat: 49.8, lng: 15.5 },
        zoom: 7,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy',
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
      })
      infoWindowRef.current = new window.google.maps.InfoWindow()
      setMapReady(true)
    }).catch((sdkError) => {
      if (!current) return
      console.error('Unable to initialize Google Maps:', sdkError)
      setError('Mapu se nepodařilo načíst.')
      setLoading(false)
    })

    return () => {
      current = false
      clustererRef.current?.clearMarkers()
      clustererRef.current?.setMap(null)
      clustererRef.current = null
      markersRef.current.forEach((marker) => marker.setMap(null))
      markersRef.current = []
      infoWindowRef.current?.close()
    }
  }, [])

  useEffect(() => {
    if (!mapReady || !mapRef.current) return undefined
    clustererRef.current?.clearMarkers()
    clustererRef.current?.setMap(null)
    clustererRef.current = null
    markersRef.current.forEach((marker) => marker.setMap(null))
    markersRef.current = points.map((point) => {
      const marker = new window.google.maps.Marker({
        position: point.coordinates,
        title: point.title,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: point.inactive ? 4 : 5,
          fillColor: point.inactive ? '#94a3b8' : '#2563eb',
          fillOpacity: point.inactive ? 0.65 : 0.85,
          strokeColor: '#ffffff',
          strokeWeight: 1.5,
        },
      })
      marker.addListener('mouseover', () => {
        infoWindowRef.current?.setContent(createInfoContent(point))
        infoWindowRef.current?.open(mapRef.current, marker)
      })
      marker.addListener('mouseout', () => infoWindowRef.current?.close())
      marker.addListener('click', () => navigate(`/detail/${point.propertyId}`))
      return marker
    })
    clustererRef.current = new MarkerClusterer({
      map: mapRef.current,
      markers: markersRef.current,
      algorithm: new SuperClusterAlgorithm({ radius: 80, maxZoom: 17 }),
    })

    return () => {
      clustererRef.current?.clearMarkers()
      clustererRef.current?.setMap(null)
      clustererRef.current = null
      markersRef.current.forEach((marker) => {
        window.google.maps.event.clearInstanceListeners(marker)
        marker.setMap(null)
      })
      markersRef.current = []
    }
  }, [mapReady, navigate, points])

  useEffect(() => {
    if (!mapReady || !mapRef.current) return undefined
    const requestId = ++viewportRequestRef.current
    const map = mapRef.current
    const viewportFilters = { region, regionLabel: getRegionLabel(region), municipalities, districts }

    if (isWholeCountrySelection(viewportFilters)) {
      map.fitBounds(CZECHIA_BOUNDS, 24)
      return undefined
    }

    const queries = getViewportQueries(viewportFilters)
    const geocoder = new window.google.maps.Geocoder()
    Promise.all(queries.map((query) => geocode(geocoder, query))).then((geometries) => {
      if (requestId !== viewportRequestRef.current) return
      const bounds = new window.google.maps.LatLngBounds()
      geometries.filter(Boolean).forEach((geometry) => {
        if (geometry.viewport) {
          bounds.extend(geometry.viewport.getNorthEast())
          bounds.extend(geometry.viewport.getSouthWest())
        } else if (geometry.location) {
          bounds.extend(geometry.location)
        }
      })
      if (!bounds.isEmpty()) map.fitBounds(bounds, 36)
      else fitToPoints(map, points)
    })

    return () => { viewportRequestRef.current += 1 }
  }, [mapReady, region, municipalitiesKey, districtsKey, municipalities, districts, points])

  return (
    <section className="relative min-h-[480px] h-[calc(100vh-180px)] overflow-hidden border-y border-slate-200 bg-slate-100">
      <div ref={elementRef} className="h-full w-full" />
      <div className="pointer-events-none absolute left-3 top-3 rounded-lg bg-white/95 px-3 py-2 text-xs font-medium text-slate-700 shadow">
        {loading ? (
          <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-blue-600" />Načítám nabídky…</span>
        ) : (
          <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-600" />{points.length.toLocaleString('cs-CZ')} {meta.truncated ? `z ${meta.total.toLocaleString('cs-CZ')}` : ''} nabídek</span>
        )}
      </div>
      {error && <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 shadow sm:right-auto">{error}</div>}
      {!loading && !error && points.length === 0 && <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-white/95 px-4 py-3 text-sm text-slate-600 shadow sm:right-auto">Ve zvolené oblasti nejsou nabídky se souřadnicemi.</div>}
    </section>
  )
}

export default MapPage
